import api from "./api";

// Types
export interface FormFolder {
  id: string;
  name: string;
  parent?: string | null;
  description?: string;
  order: number;
  forms_count?: number;
  created_at: string;
  updated_at: string;
}

export interface FormField {
  id?: string;
  form?: string;
  field_type:
    | "text"
    | "email"
    | "phone"
    | "number"
    | "textarea"
    | "select"
    | "radio"
    | "checkbox"
    | "multi_checkbox"
    | "date"
    | "time"
    | "datetime"
    | "file"
    | "hidden"
    | "heading"
    | "divider"
    | "paragraph";
  name: string;
  crm_field?: string;
  label: string;
  placeholder?: string;
  help_text?: string;
  default_value?: string;
  required: boolean;
  validation_rules?: Record<string, any>;
  options?: Array<{ label: string; value: string }>;
  order: number;
  width: "full" | "half" | "third" | "two_thirds";
  conditional_logic?: Record<string, any>;
}

export interface Form {
  id: string;
  embed_id: string;
  name: string;
  description?: string;
  folder?: string | null;
  folder_name?: string;
  status: "draft" | "active" | "archived";
  form_schema: {
    fields?: FormField[];
    layout?: any;
  };
  fields?: FormField[];
  submit_button_text: string;
  submit_action: "redirect" | "message" | "download";
  submit_action_value?: string;
  success_message: string;
  styling?: Record<string, any>;
  email_notification_enabled: boolean;
  email_notification_to: string[];
  email_notification_subject?: string;
  email_notification_reply_to?: string;
  facebook_pixel_id?: string;
  google_analytics_id?: string;
  allowed_embed_domains?: string[];
  create_contact: boolean;
  contact_source?: string;
  contact_source_name?: string;
  usage_count: number;
  last_submitted?: string;
  submissions_count?: number;
  created_by?: any;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  embed_url?: string;
}

export interface FormSubmission {
  id: string;
  form: string;
  form_name?: string;
  data: Record<string, any>;
  files?: Record<string, any>;
  contact?: string;
  contact_name?: string;
  display_data?: Array<{ label: string; value: any }>;
  ip_address?: string;
  user_agent?: string;
  referrer_url?: string;
  page_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  submitted_at: string;
  processed: boolean;
  processing_notes?: string;
  embedding_domain?: string;
  embed_flagged?: boolean;
}

export interface EmbedCode {
  iframe: string;
  script: string;
  direct_url: string;
}

// API Functions

// Form Folders
export const formFoldersApi = {
  list: (params?: { parent?: string; page?: number }) => {
    const queryString = params
      ? new URLSearchParams(params as any).toString()
      : "";
    return api.get<{
      results: FormFolder[];
      count: number;
      next: string | null;
    }>(`/api/marketing/forms/folders/${queryString ? "?" + queryString : ""}`);
  },

  create: (data: Partial<FormFolder>) =>
    api.post<FormFolder>("/api/marketing/forms/folders/", data),

  update: (id: string, data: Partial<FormFolder>) =>
    api.patch<FormFolder>(`/api/marketing/forms/folders/${id}/`, data),

  delete: (id: string) => api.delete(`/api/marketing/forms/folders/${id}/`),

  reorder: (folder_ids: string[]) =>
    api.post("/api/marketing/forms/folders/reorder/", { folder_ids }),
};

// Forms
export const formsApi = {
  list: (params?: {
    folder?: string;
    status?: string;
    search?: string;
    page?: number;
  }) => {
    // Filter out undefined values to prevent URLSearchParams from converting them to string "undefined"
    const filteredParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v !== undefined),
        )
      : {};
    const queryString = new URLSearchParams(filteredParams as any).toString();
    return api.get<{ results: Form[]; count: number; next: string | null }>(
      `/api/marketing/forms/${queryString ? "?" + queryString : ""}`,
    );
  },

  get: (id: string) => api.get<Form>(`/api/marketing/forms/${id}/`),

  create: (data: Partial<Form>) =>
    api.post<Form>("/api/marketing/forms/", data),

  update: (id: string, data: Partial<Form>) =>
    api.patch<Form>(`/api/marketing/forms/${id}/`, data),

  delete: (id: string) => api.delete(`/api/marketing/forms/${id}/`),

  duplicate: (
    id: string,
    data: { name: string; folder?: string; copy_submissions?: boolean },
  ) => api.post<Form>(`/api/marketing/forms/${id}/duplicate/`, data),

  getEmbedCode: (id: string) =>
    api.get<EmbedCode>(`/api/marketing/forms/${id}/embed-code/`),

  getSubmissions: (
    id: string,
    params?: { page?: number; search?: string; processed?: string },
  ) => {
    const queryString = params
      ? new URLSearchParams(params as any).toString()
      : "";
    return api.get<{
      results: FormSubmission[];
      count: number;
      next: string | null;
    }>(
      `/api/marketing/forms/${id}/submissions/${queryString ? "?" + queryString : ""}`,
    );
  },

  exportSubmissions: (id: string, params: Record<string, string> = {}) =>
    api.get<string>(
      `/api/marketing/forms/${id}/export-submissions/?${new URLSearchParams(params)}`,
    ),

  testSubmission: (
    id: string,
    form_schema: Form["form_schema"],
    data: Record<string, unknown>,
  ) =>
    api.post<{ message: string }>(
      `/api/marketing/forms/${id}/test-submission/`,
      { form_schema, data },
    ),

  updateSchema: (id: string, form_schema: any) =>
    api.patch<Form>(`/api/marketing/forms/${id}/update-schema/`, {
      form_schema,
    }),

  toggleStatus: (id: string) =>
    api.post<Form>(`/api/marketing/forms/${id}/toggle-status/`, {}),
};

// Form Fields
export const formFieldsApi = {
  list: (form: string) =>
    api.get<{ results: FormField[] }>(
      `/api/marketing/form-fields/?form=${form}`,
    ),

  create: (data: FormField) =>
    api.post<FormField>("/api/marketing/form-fields/", data),

  update: (id: string, data: Partial<FormField>) =>
    api.patch<FormField>(`/api/marketing/form-fields/${id}/`, data),

  delete: (id: string) => api.delete(`/api/marketing/form-fields/${id}/`),

  bulkUpdate: (fields: Array<{ id: string; order: number; width: string }>) =>
    api.post("/api/marketing/form-fields/bulk-update/", { fields }),
};

// Form Submissions
export const formSubmissionsApi = {
  list: (params?: { form?: string; page?: number }) => {
    const queryString = params
      ? new URLSearchParams(params as any).toString()
      : "";
    return api.get<{
      results: FormSubmission[];
      count: number;
      next: string | null;
    }>(
      `/api/marketing/form-submissions/${queryString ? "?" + queryString : ""}`,
    );
  },

  get: (id: string) =>
    api.get<FormSubmission>(`/api/marketing/form-submissions/${id}/`),

  markProcessed: (id: string, notes?: string) =>
    api.patch<FormSubmission>(
      `/api/marketing/form-submissions/${id}/mark-processed/`,
      { notes },
    ),

  delete: (id: string) => api.delete(`/api/marketing/form-submissions/${id}/`),
};

// Public Form API (no auth required)
export const publicFormApi = {
  getForm: (formId: string) =>
    fetch(`/api/marketing/forms/public/${formId}/`).then((res) => {
      if (!res.ok) {
        throw new Error("Form not found or not available");
      }
      return res.json();
    }),

  submitForm: (data: {
    form_id: string;
    data: Record<string, any>;
    files?: Record<string, any>;
    page_url?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  }) =>
    fetch("/api/marketing/forms/public/submit/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }).then(async (res) => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const details =
          body.error ||
          body.detail ||
          (body.data &&
            Object.entries(body.data)
              .map(([name, message]) => `${name}: ${message}`)
              .join("; "));
        throw new Error(
          details ||
            (res.status === 429
              ? "Too many submissions. Please try again later."
              : "Unable to submit. Please try again."),
        );
      }
      return body;
    }),
};

// Helper functions
export const downloadSubmissionsCSV = (
  formId: string,
  formName: string,
  params: Record<string, string> = {},
) => {
  return formsApi.exportSubmissions(formId, params).then((response: any) => {
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${formName}_submissions_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  });
};
